import React, { useEffect, useState, useCallback } from 'react';
import Button from '../components/ui/Button';
import CenterMessage from '../components/ui/CenterMessage';
import DisclosureBadge from '../components/epr/DisclosureBadge';
import AppraisalSummaryView from '../components/epr/AppraisalSummaryView';
import RecipientBanner from '../components/magic/RecipientBanner';
import { useToast } from '../components/ui/Toast';
import type { AppraisalForm } from '../types/appraisal';
import type { AppealRecord } from '../types/appeal';
import { APPEAL_FILING_WINDOW_DAYS } from '../types/appeal';
import type { MagicLinkToken } from '../types/magicLinkToken';
import { getAppraisal } from '../services/appraisalService';
import {
  fileAppeal, getAppealByAppraisal, isAppealWindowOpen, AppealError,
} from '../services/appealService';

/**
 * Result view — read-only appraisal accessed via reusable magic link.
 *
 * When viewed by the employee within the appeal window (≤ 2 days after
 * manager sign-off), this page also exposes a "File an appeal" form so
 * the employee can lodge a dispute without HR intervention. Maps to the
 * Performance Review Framework §G.3 Week 6-7.
 */

const AppraisalViewMagicForm: React.FC<{ token: MagicLinkToken }> = ({ token }) => {
  const toast = useToast();
  const [appraisal, setAppraisal] = useState<AppraisalForm | null>(null);
  const [appeal,    setAppeal]    = useState<AppealRecord | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const a = await getAppraisal(token.targetDocId);
        if (!alive) return;
        setAppraisal(a);
        if (a) {
          const existing = await getAppealByAppraisal(a.id);
          if (alive) setAppeal(existing);
        }
      } catch (e) {
        if (alive) setError((e as Error).message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token.targetDocId, refreshTick]);

  if (loading) return <CenterMessage tone="info" title="Loading appraisal..." />;
  if (error || !appraisal) {
    return (
      <CenterMessage
        tone="error"
        title="Cannot display appraisal"
        body={error ?? 'No appraisal found for this link.'}
      />
    );
  }

  const isEmployeeView   = token.recipientRole === 'employee';
  const isManagerSigned  = !!appraisal.managerSignature;
  const windowOpen       = isManagerSigned && isAppealWindowOpen(appraisal);
  const canFileAppeal    = isEmployeeView && isManagerSigned && windowOpen && !appeal;

  return (
    <div className="min-h-screen bg-stone-50">
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-stone-500">Performance Appraisal</div>
            <div className="text-base font-semibold text-stone-900">
              {token.recipientName ?? token.recipientEmail}
            </div>
            <div className="text-xs text-stone-500">
              Cycle: {appraisal.cycleId} · Status: {appraisal.status}
            </div>
          </div>
          {appeal && (
            <DisclosureBadge
              icon="gavel"
              label={`Appeal: ${appeal.status}`}
              tone={appeal.status === 'decided' ? 'success' : 'warning'}
            />
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 pb-12">
        {/* Identity banner — read-only result view, but still helpful so
            an accidental forward recipient sees they're not the owner. */}
        <RecipientBanner
          recipientEmail={token.recipientEmail}
          recipientName={token.recipientName}
        />
        <AppraisalSummaryView appraisal={appraisal} />

        {appeal && <AppealStatusCard appeal={appeal} />}

        {canFileAppeal && (
          <AppealFilingPanel
            appraisal={appraisal}
            employeeId={token.recipientId}
            onFiled={() => {
              toast.show('Appeal filed. HR will reach out to schedule the meeting.', { icon: 'check' });
              setRefreshTick(t => t + 1);
            }}
          />
        )}

        {!canFileAppeal && isEmployeeView && isManagerSigned && !appeal && !windowOpen && (
          <section className="rounded-xl border border-stone-200 bg-stone-50 p-5 text-sm text-stone-600">
            The {APPEAL_FILING_WINDOW_DAYS}-day appeal window has closed. Contact HR if you have further questions.
          </section>
        )}
      </main>
    </div>
  );
};

// ─── Filing panel (employee-side) ────────────────────────────────

const AppealFilingPanel: React.FC<{
  appraisal: AppraisalForm;
  employeeId: string;
  onFiled: () => void;
}> = ({ appraisal, employeeId, onFiled }) => {
  const toast = useToast();
  const [open,   setOpen]   = useState(false);
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);

  const submit = useCallback(async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 20) {
      toast.show('Please write at least 20 characters explaining the appeal reason.', { icon: 'warning' });
      return;
    }
    setBusy(true);
    try {
      await fileAppeal({
        cycleId:     appraisal.cycleId,
        employeeId,
        appraisalId: appraisal.id,
        filedReason: trimmed,
      });
      onFiled();
    } catch (e) {
      const err = e as AppealError | Error;
      toast.show(`Failed: ${err.message}`, { icon: 'error' });
    } finally { setBusy(false); }
  }, [reason, appraisal, employeeId, toast, onFiled]);

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50 p-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="material-symbols-rounded text-amber-700" style={{ fontSize: 20 }}>gavel</span>
          <h3 className="text-base font-semibold text-amber-900">File an appeal</h3>
        </div>
        {!open && (
          <Button variant="secondary" icon="edit_note" onClick={() => setOpen(true)}>
            Open appeal form
          </Button>
        )}
      </div>

      {!open && (
        <p className="text-sm text-amber-900 mt-3 leading-relaxed">
          If you do not agree with this appraisal result, you can file an
          appeal within {APPEAL_FILING_WINDOW_DAYS} days of your manager's
          signature. HR will facilitate an appeal meeting (manager +
          employee + HR ± peers) within 2 working days.
        </p>
      )}

      {open && (
        <div className="mt-4 space-y-3">
          <label className="block text-sm font-medium text-amber-900">
            Appeal reason
            <span className="ml-1 text-amber-700 font-normal">(at least 20 characters; be specific)</span>
          </label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={5}
            disabled={busy}
            placeholder="e.g. I do not agree with the KPI score for Q2 because the target was revised mid-cycle and the actual achievement was 105% of the original target, not the revised one."
            className="w-full rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
          />
          <div className="text-xs text-amber-800">
            By filing this appeal, you formally request HR to facilitate a
            review meeting. The decision (uphold / partially adjust / fully
            revise) will be issued by HR after the meeting.
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-amber-200">
            <Button variant="tertiary" onClick={() => { setOpen(false); setReason(''); }} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="submit"
              icon="gavel"
              loading={busy}
              disabled={reason.trim().length < 20}
              onClick={submit}
            >
              File appeal
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

// ─── Existing appeal status (read-only) ──────────────────────────

const AppealStatusCard: React.FC<{ appeal: AppealRecord }> = ({ appeal }) => (
  <section className="rounded-xl border border-stone-200 bg-white p-5">
    <div className="flex items-center gap-2 mb-2">
      <span className="material-symbols-rounded text-stone-600" style={{ fontSize: 20 }}>gavel</span>
      <h3 className="text-base font-semibold text-stone-900">Appeal status</h3>
      <DisclosureBadge
        label={appeal.status}
        tone={appeal.status === 'decided' ? 'success'
            : appeal.status === 'in_meeting' ? 'info'
            : 'warning'}
      />
    </div>
    <div className="text-xs text-stone-500 mb-3">
      Filed: {appeal.filedAt?.toDate ? appeal.filedAt.toDate().toLocaleString('en-GB') : '—'}
    </div>
    <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-sm mb-3">
      <div className="text-xs font-medium text-stone-500 mb-1">Your reason:</div>
      <div className="whitespace-pre-wrap text-stone-800">{appeal.filedReason}</div>
    </div>
    {appeal.decision && (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
        <div className="text-xs font-medium text-emerald-700 mb-1">
          HR Decision: {appeal.decision.replace(/_/g, ' ')}
        </div>
        {appeal.decisionNotes && (
          <div className="whitespace-pre-wrap text-emerald-900 mt-1">{appeal.decisionNotes}</div>
        )}
      </div>
    )}
  </section>
);

export default AppraisalViewMagicForm;
